/**
 * EntityInputGlobal - Vanilla JS wrapper for EntityInput React component
 * 
 * This wrapper provides an imperative API for non-React environments,
 * specifically for the Graph extension which needs:
 * - Constructor with callback options (onSearch, onSubmit, onEntitySelect, onChange)
 * - init(container) method to mount into DOM
 * - setSuggestions(suggestions) method
 * - getPlainText(), getFullText()/getFullQuery(), getEntities()/getSelectedEntities() methods
 * - clear(), focus(), setTheme(), destroy() methods
 * 
 * Usage:
 *   const input = new EntityInput({
 *     placeholder: 'Search...',
 *     onSearch: (query) => fetchSuggestions(query),
 *     onSubmit: (data) => executeSearch(data.query, data.keywords),
 *   });
 *   input.init(document.getElementById('container'));
 *   input.setSuggestions([{ id: '1', label: 'Node', type: 'node' }]);
 */

import './components/EntityInput.css';
import { safeSetInnerHTML, safeInsertAdjacentHTML } from '@merlin/lib/js/trusted-types.js';

/**
 * EntityInput - Vanilla JS class with imperative API
 * 
 * This is a self-contained implementation that mirrors the React component's
 * functionality but with a callback-based API suitable for non-React usage.
 */
export class EntityInputGlobal {
    constructor(options = {}) {
        this.options = {
            placeholder: 'Search entities or ask a question...',
            debounceMs: 300,
            minSearchLength: 2,
            theme: 'light',
            dropdownPosition: 'below',
            searchTriggerChar: null,
            // When false, badges are shown without a visible # prefix in the input/output.
            prefixBadgeWithTrigger: true,
            showAnalyzeButton: true,
            onSearch: null,
            onSubmit: null,
            onEntitySelect: null,
            onChange: null,
            onAnalyze: null,
            onEntityLinkClick: null,
            ...options
        };
        
        this.container = null;
        this.inputEl = null;
        this.suggestionsEl = null;
        this.analyzeBtnEl = null;
        this.suggestions = [];
        this.highlightedIndex = 0;
        this.debounceTimer = null;
        this.showSuggestions = false;
        this._blurTimeout = null;
        // Staleness tracking: the search term that was last dispatched
        // via onSearch. setSuggestions() compares incoming results against
        // the current phrase — if they diverge the suggestions are discarded.
        this._lastSearchTerm = '';
    }
    
    /**
     * Initialize and render the component into a container
     * @param {HTMLElement} container - The DOM element to render into
     * @returns {EntityInputGlobal} this for chaining
     */
    init(container) {
        if (typeof container === 'string') {
            container = document.querySelector(container);
        }
        this.container = container;
        this._render();
        this._attachListeners();
        return this;
    }
    
    /**
     * Create the DOM structure
     */
    _render() {
        // Create container
        const wrapper = document.createElement('div');
        wrapper.className = 'entity-input-container';
        wrapper.dataset.theme = this.options.theme;
        wrapper.dataset.dropdown = this.options.dropdownPosition;
        
        // Create row wrapper for input + analyze button
        const row = document.createElement('div');
        row.className = 'entity-input-row';

        // Create input
        this.inputEl = document.createElement('div');
        this.inputEl.className = 'entity-input';
        this.inputEl.contentEditable = 'true';
        this.inputEl.dataset.placeholder = this.options.placeholder;
        this.inputEl.setAttribute('role', 'textbox');
        this.inputEl.setAttribute('aria-multiline', 'false');
        this.inputEl.setAttribute('aria-label', this.options.placeholder);
        
        // Create analyze (magic wand) button
        this.analyzeBtnEl = document.createElement('button');
        this.analyzeBtnEl.className = 'entity-analyze-btn';
        this.analyzeBtnEl.type = 'button';
        this.analyzeBtnEl.title = 'Analyze entire text for entities';
        this.analyzeBtnEl.setAttribute('aria-label', 'Analyze text');
        safeSetInnerHTML(this.analyzeBtnEl, `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M15 4V2" />
                <path d="M15 16v-2" />
                <path d="M8 9h2" />
                <path d="M20 9h2" />
                <path d="M17.8 11.8L19 13" />
                <path d="M15 9h0" />
                <path d="M17.8 6.2L19 5" />
                <path d="M3 21l9-9" />
                <path d="M12.2 6.2L11 5" />
            </svg>
        `);

        // Create suggestions dropdown
        this.suggestionsEl = document.createElement('div');
        this.suggestionsEl.className = 'entity-suggestions';
        this.suggestionsEl.style.display = 'none';
        
        row.appendChild(this.inputEl);
        if (this.options.showAnalyzeButton) {
            row.appendChild(this.analyzeBtnEl);
        } else if (this.analyzeBtnEl) {
            this.analyzeBtnEl = null;
        }
        wrapper.appendChild(row);
        wrapper.appendChild(this.suggestionsEl);
        
        safeSetInnerHTML(this.container, '');
        this.container.appendChild(wrapper);
        this.wrapperEl = wrapper;
    }
    
    /**
     * Attach event listeners
     */
    _attachListeners() {
        this.inputEl.addEventListener('input', this._handleInput.bind(this));
        this.inputEl.addEventListener('keydown', this._handleKeyDown.bind(this));
        this.inputEl.addEventListener('paste', this._handlePaste.bind(this));
        this.inputEl.addEventListener('focus', this._handleFocus.bind(this));
        this.inputEl.addEventListener('blur', this._handleBlur.bind(this));
        if (this.analyzeBtnEl) {
            this.analyzeBtnEl.addEventListener('click', this._handleAnalyzeClick.bind(this));
        }
        
        // Click outside to close suggestions
        document.addEventListener('click', (e) => {
            if (!this.wrapperEl.contains(e.target)) {
                this._hideSuggestions();
            }
        });
    }
    
    /**
     * Handle input events with debounced search.
     * Sends multi-word phrase for better entity matching.
     * Uses adaptive debounce: shorter for short terms, longer for phrases.
     */
    _handleInput(e) {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
        
        const searchContext = this._getActiveSearchContext();
        const searchTerm = searchContext.searchTerm;
        
        // Fire onChange callback
        if (this.options.onChange) {
            this.options.onChange({
                query: this.getFullText(),
                keywords: this.getEntities(),
                plainText: this.getPlainText(),
            });
        }
        
        // Debounced search
        if (searchContext.active && searchTerm.length >= this.options.minSearchLength) {
            // Eagerly clear stale suggestions when the search term diverges
            const prevFirst = (this._lastSearchTerm || '').split(/\s+/)[0]?.toLowerCase();
            const currFirst = searchTerm.split(/\s+/)[0]?.toLowerCase();
            if (prevFirst && currFirst && !currFirst.startsWith(prevFirst) && !prevFirst.startsWith(currFirst)) {
                this.suggestions = [];
                this._hideSuggestions();
            }

            // Adaptive debounce: faster for short single words
            const delay = searchTerm.length <= 4
                ? Math.min(this.options.debounceMs, 150)
                : this.options.debounceMs;

            this.debounceTimer = setTimeout(() => {
                this._lastSearchTerm = searchTerm;
                if (this.options.onSearch) {
                    this.options.onSearch(searchTerm);
                }
            }, delay);
        } else {
            this.suggestions = [];
            this._lastSearchTerm = '';
            this._hideSuggestions();
        }
    }
    
    /**
     * Handle keyboard navigation
     */
    _handleKeyDown(e) {
        if (this.showSuggestions && this.suggestions.length > 0) {
            switch (e.key) {
                case 'Tab':
                    e.preventDefault();
                    this._acceptSuggestion(this.suggestions[this.highlightedIndex]);
                    break;
                    
                case 'Enter':
                    e.preventDefault();
                    this._acceptSuggestion(this.suggestions[this.highlightedIndex]);
                    break;
                    
                case 'ArrowDown':
                    e.preventDefault();
                    this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.suggestions.length - 1);
                    this._renderSuggestions();
                    break;
                    
                case 'ArrowUp':
                    e.preventDefault();
                    this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
                    this._renderSuggestions();
                    break;
                    
                case 'Escape':
                    e.preventDefault();
                    // Clear state completely so re-focus doesn't re-show stale dropdown
                    this.suggestions = [];
                    this._lastSearchTerm = '';
                    this._hideSuggestions();
                    break;
            }
        } else if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (this.options.onSubmit) {
                this.options.onSubmit({
                    query: this.getFullText(),
                    keywords: this.getEntities(),
                    plainText: this.getPlainText(),
                });
            }
        } else if (e.key === 'Backspace') {
            this._handleBackspace(e);
        }
    }
    
    /**
     * Handle backspace to delete badges
     */
    _handleBackspace(e) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            if (range.collapsed && range.startOffset === 0) {
                const prevSibling = range.startContainer.previousSibling;
                if (prevSibling && this._isEntityChip(prevSibling)) {
                    e.preventDefault();
                    this._removeEntityChip(prevSibling);
                    if (this.options.onChange) {
                        this.options.onChange({
                            query: this.getFullText(),
                            keywords: this.getEntities(),
                            plainText: this.getPlainText(),
                        });
                    }
                }
            }
        }
    }
    
    /**
     * Handle paste - strip formatting
     */
    _handlePaste(e) {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text);
    }
    
    /**
     * Handle analyze (magic wand) button click
     */
    _handleAnalyzeClick(e) {
        e.preventDefault();
        if (this.options.onAnalyze) {
            this.options.onAnalyze({
                query: this.getFullText(),
                plainText: this.getPlainText(),
                keywords: this.getEntities(),
            });
        }
    }

    /**
     * Handle focus
     */
    _handleFocus(e) {
        // Only re-show suggestions if there's actual text being searched
        const plainText = this.getPlainText();
        if (this.suggestions.length > 0 && plainText.length >= this.options.minSearchLength) {
            this._showSuggestions();
        }
    }
    
    /**
     * Handle blur
     */
    _handleBlur(e) {
        // Delay hiding to allow click on suggestions
        this._blurTimeout = setTimeout(() => {
            this._hideSuggestions();
        }, 150);
    }
    
    /**
     * Resolve the active search term, honoring optional trigger characters (e.g. '#').
     */
    _getActiveSearchContext() {
        const trigger = this.options.searchTriggerChar;
        if (!trigger) {
            const { phrase, word } = this._getCurrentPhrase();
            const searchTerm = phrase.length > word.length ? phrase : word;
            return {
                active: true,
                searchTerm,
                phrase,
                word,
                phraseStartOffset: null,
                wordStartOffset: null,
                textNode: null,
                triggerActive: false,
            };
        }

        const { phrase, word, phraseStartOffset, wordStartOffset, textNode } = this._getCurrentPhrase();
        if (!textNode) {
            return { active: false, searchTerm: '', phrase: '', word: '', triggerActive: false };
        }

        const text = textNode.textContent || '';
        const selection = window.getSelection();
        const cursorPos = selection?.rangeCount ? selection.getRangeAt(0).startOffset : text.length;
        const beforeCursor = text.substring(0, cursorPos);
        const hashIndex = beforeCursor.lastIndexOf(trigger);
        if (hashIndex === -1) {
            return { active: false, searchTerm: '', phrase: '', word: '', triggerActive: false };
        }

        const afterHash = beforeCursor.slice(hashIndex + 1);
        const segmentWordMatch = afterHash.match(/(\S+)$/);
        const segmentWord = segmentWordMatch ? segmentWordMatch[1] : '';
        const segmentPhraseMatch = afterHash.match(/(?:\S+\s+){0,2}\S+$/);
        const segmentPhrase = segmentPhraseMatch ? segmentPhraseMatch[0].trim() : segmentWord;
        const searchTerm = segmentPhrase.length > segmentWord.length ? segmentPhrase : segmentWord;

        const hashRelativeWordStart = hashIndex + 1 + afterHash.length - segmentWord.length;
        const hashRelativePhraseStart = hashIndex + 1 + afterHash.length - segmentPhrase.length;

        return {
            active: true,
            searchTerm,
            phrase: segmentPhrase,
            word: segmentWord,
            phraseStartOffset: hashRelativePhraseStart,
            wordStartOffset: hashRelativeWordStart,
            textNode,
            triggerActive: true,
            triggerChar: trigger,
        };
    }

    /**
     * Get the current word being typed
     */
    _getCurrentWord() {
        const selection = window.getSelection();
        if (!selection.rangeCount) return { word: '', startOffset: 0, endOffset: 0 };
        
        const range = selection.getRangeAt(0);
        if (range.startContainer.nodeType !== Node.TEXT_NODE) {
            return { word: '', startOffset: 0, endOffset: 0, textNode: null };
        }
        
        const text = range.startContainer.textContent;
        const cursorPos = range.startOffset;
        
        let start = cursorPos;
        let end = cursorPos;
        
        while (start > 0 && !/\s/.test(text[start - 1])) {
            start--;
        }
        while (end < text.length && !/\s/.test(text[end])) {
            end++;
        }
        
        return {
            word: text.substring(start, cursorPos),
            startOffset: start,
            endOffset: end,
            textNode: range.startContainer,
        };
    }
    
    /**
     * Get the current phrase (up to 3 trailing words) for multi-word matching.
     */
    _getCurrentPhrase() {
        const selection = window.getSelection();
        if (!selection.rangeCount) return { phrase: '', word: '', phraseStartOffset: 0, wordStartOffset: 0, textNode: null };
        
        const range = selection.getRangeAt(0);
        if (range.startContainer.nodeType !== Node.TEXT_NODE) {
            return { phrase: '', word: '', phraseStartOffset: 0, wordStartOffset: 0, textNode: null };
        }
        
        const text = range.startContainer.textContent;
        const cursorPos = range.startOffset;
        const beforeCursor = text.substring(0, cursorPos);
        
        const wordMatch = beforeCursor.match(/(\S+)$/);
        const word = wordMatch ? wordMatch[1] : '';
        const wordStartOffset = cursorPos - word.length;
        
        const phraseMatch = beforeCursor.match(/(?:\S+\s+){0,2}\S+$/);
        const phrase = phraseMatch ? phraseMatch[0].trim() : word;
        const phraseStartOffset = phraseMatch
            ? cursorPos - phraseMatch[0].trimStart().length
            : wordStartOffset;
        
        return { phrase, word, phraseStartOffset, wordStartOffset, textNode: range.startContainer };
    }

    _isEntityChip(node) {
        return Boolean(
            node?.classList?.contains('entity-link-chip')
            || node?.classList?.contains('entity-badge'),
        );
    }

    _readEntityFromChip(chip) {
        if (!chip) return null;
        if (chip.classList.contains('entity-badge')) {
            return {
                id: chip.dataset.entityId,
                label: chip.dataset.entityLabel,
                type: chip.dataset.entityType || 'node',
                matchedText: chip.dataset.matchedText || chip.dataset.entityLabel || '',
            };
        }
        const link = chip.querySelector('.entity-graph-link');
        if (!link) return null;
        const id = link.dataset.nodeId
            || link.dataset.typeId
            || link.dataset.graphId
            || '';
        const type = chip.dataset.entityType
            || link.dataset.graphKind
            || (link.dataset.nodeId ? 'node' : link.dataset.typeId ? 'ontology_type' : 'taxonomy_resource');
        return {
            id,
            label: chip.dataset.entityLabel || link.textContent || '',
            type,
            matchedText: chip.dataset.matchedText || chip.dataset.entityLabel || link.textContent || '',
        };
    }

    _entityLinkConfig(entityType, entityId) {
        const kind = EntityInputGlobal.toMarkdownLinkType(entityType);
        if (kind === 'node') {
            return {
                className: 'entity-graph-link a2a-node-link',
                attrs: { 'data-node-id': entityId },
            };
        }
        if (kind === 'edge') {
            return {
                className: 'entity-graph-link a2a-type-link',
                attrs: { 'data-type-id': entityId },
            };
        }
        return {
            className: 'entity-graph-link a2a-graph-link',
            attrs: {
                'data-graph-kind': kind,
                'data-graph-id': entityId,
            },
        };
    }

    _createEntityChip({ id, label, type, matchedText }) {
        const chip = document.createElement('span');
        chip.className = 'entity-link-chip';
        chip.contentEditable = 'false';
        chip.dataset.entityId = id;
        chip.dataset.entityLabel = label;
        chip.dataset.entityType = type || 'node';
        chip.dataset.matchedText = matchedText || label || '';

        const link = document.createElement('a');
        link.href = '#';
        link.className = this._entityLinkConfig(type, id).className;
        const attrs = this._entityLinkConfig(type, id).attrs;
        Object.entries(attrs).forEach(([key, value]) => {
            link.setAttribute(key, value);
        });
        link.textContent = matchedText || label || '';
        link.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const entity = this._readEntityFromChip(chip);
            if (this.options.onEntityLinkClick) {
                this.options.onEntityLinkClick(entity);
            }
        });

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'entity-remove';
        removeBtn.title = 'Remove link';
        removeBtn.setAttribute('aria-label', 'Remove link');
        removeBtn.textContent = '×';
        removeBtn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this._removeEntityChip(chip);
            if (this.options.onChange) {
                this.options.onChange({
                    query: this.getFullText(),
                    keywords: this.getEntities(),
                    plainText: this.getPlainText(),
                });
            }
        });

        chip.appendChild(link);
        chip.appendChild(document.createTextNode(' '));
        chip.appendChild(removeBtn);
        return chip;
    }

    _removeEntityChip(chip) {
        if (!chip?.parentNode) return;
        const entity = this._readEntityFromChip(chip);
        const restoreText = entity?.matchedText || entity?.label || '';
        const textNode = document.createTextNode(restoreText);
        chip.parentNode.insertBefore(textNode, chip);
        chip.remove();
    }

    _forEachEntityNode(callback) {
        if (!this.inputEl) return;
        this.inputEl.childNodes.forEach((node) => {
            if (this._isEntityChip(node)) {
                callback(node);
            }
        });
    }
    
    /**
     * Accept a suggestion and insert as an inline entity link.
     * Uses phrase-aware replacement so multi-word typed text is cleanly replaced.
     */
    _acceptSuggestion(entity) {
        const searchContext = this._getActiveSearchContext();
        const {
            phrase,
            word,
            phraseStartOffset,
            wordStartOffset,
            textNode,
            triggerActive,
            triggerChar,
        } = searchContext.active
            ? searchContext
            : this._getCurrentPhrase();
        
        if (!textNode || !this.inputEl) {
            this._hideSuggestions();
            return;
        }
        
        // Decide how much text to replace
        let replaceStart = wordStartOffset;
        let replaceLen = word.length;
        
        if (phrase.length > word.length) {
            const phraseWords = phrase.toLowerCase().split(/\s+/);
            const entityWords = entity.label.toLowerCase().split(/\s+/);
            let matchesPhrase = phraseWords.length <= entityWords.length;
            if (matchesPhrase) {
                for (let i = 0; i < phraseWords.length; i++) {
                    if (!entityWords[i].startsWith(phraseWords[i])) {
                        matchesPhrase = false;
                        break;
                    }
                }
            }
            if (!matchesPhrase && entity.label.toLowerCase().includes(phrase.toLowerCase())) {
                matchesPhrase = true;
            }
            if (matchesPhrase) {
                replaceStart = phraseStartOffset;
                replaceLen = phrase.length;
            }
        }
        
        // Create inline entity link chip (restore typed text on unlink)
        const nodeText = textNode.textContent || '';
        const matchedText = nodeText.substring(replaceStart, replaceStart + replaceLen);
        const chip = this._createEntityChip({
            id: entity.id,
            label: entity.label,
            type: entity.type || 'node',
            matchedText,
        });
        
        // Replace typed text with link chip
        let beforeText;
        if (triggerActive && triggerChar) {
            const selection = window.getSelection();
            const cursorPos = selection?.rangeCount ? selection.getRangeAt(0).startOffset : nodeText.length;
            const hashIndex = nodeText.substring(0, cursorPos).lastIndexOf(triggerChar);
            beforeText = hashIndex >= 0 ? nodeText.substring(0, hashIndex) : nodeText.substring(0, replaceStart);
        } else {
            beforeText = nodeText.substring(0, replaceStart);
        }
        let afterText = nodeText.substring(replaceStart + replaceLen);

        // Trim leftover word fragments that could pollute the next
        // _getCurrentPhrase() call (same fix as React component).
        afterText = afterText.replace(/^\S*/, '').trimStart();

        const beforeNode = document.createTextNode(beforeText);
        const hashPrefixNode = triggerActive && triggerChar && this.options.prefixBadgeWithTrigger
            ? document.createTextNode(triggerChar)
            : null;
        const afterNode = document.createTextNode(' ' + afterText);
        
        const parent = textNode.parentNode;
        parent.insertBefore(beforeNode, textNode);
        if (hashPrefixNode) {
            parent.insertBefore(hashPrefixNode, textNode);
        }
        parent.insertBefore(chip, textNode);
        parent.insertBefore(afterNode, textNode);
        parent.removeChild(textNode);
        
        // Set cursor after badge
        const range = document.createRange();
        const sel = window.getSelection();
        range.setStart(afterNode, 1);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        
        // Hide suggestions
        this._hideSuggestions();
        
        // Fire callbacks
        if (this.options.onEntitySelect) {
            this.options.onEntitySelect(entity);
        }
        if (this.options.onChange) {
            this.options.onChange({
                query: this.getFullText(),
                keywords: this.getEntities(),
                plainText: this.getPlainText(),
            });
        }
    }
    
    /**
     * Render suggestions dropdown
     */
    _renderSuggestions() {
        if (!this.suggestions.length) {
            this._hideSuggestions();
            return;
        }
        
        const typeIcons = {
            node: '●',
            edge: '↔',
            concept: '◇',
            taxonomy_resource: '◆',
            ontology_type: '⬡',
            person: '👤',
            organization: '🏢',
            location: '📍',
            event: '📅',
            default: '○',
        };
        
        safeSetInnerHTML(this.suggestionsEl, this.suggestions.map((entity, index) => `
            <div class="entity-suggestion ${index === this.highlightedIndex ? 'highlighted' : ''}" data-index="${index}">
                <span class="entity-type-icon entity-type-${entity.type || 'default'}">
                    ${typeIcons[entity.type] || typeIcons.default}
                </span>
                <span class="entity-suggestion-label">${entity.label}</span>
                ${entity.description ? `<span class="entity-suggestion-desc">${entity.description}</span>` : ''}
                <span class="entity-suggestion-hint">TAB</span>
            </div>
        `).join(''));
        
        // Add click handlers
        this.suggestionsEl.querySelectorAll('.entity-suggestion').forEach((el) => {
            el.addEventListener('mousedown', (e) => {
                e.preventDefault();
                const idx = parseInt(el.dataset.index);
                this._acceptSuggestion(this.suggestions[idx]);
            });
            el.addEventListener('mouseenter', () => {
                this.highlightedIndex = parseInt(el.dataset.index);
                this._renderSuggestions();
            });
        });
        
        this._showSuggestions();
    }
    
    /**
     * Show suggestions dropdown
     */
    _showSuggestions() {
        this.showSuggestions = true;
        this.suggestionsEl.style.display = 'block';
    }
    
    /**
     * Hide suggestions dropdown
     */
    _hideSuggestions() {
        this.showSuggestions = false;
        this.suggestionsEl.style.display = 'none';
    }
    
    // ========================================
    // Public API Methods
    // ========================================
    
    /**
     * Stopwords excluded from relevance filtering.
     */
    static _STOPWORDS = new Set([
        'a','an','the','for','of','in','on','to','and','or',
        'is','are','was','were','be','been','with','at','by',
        'from','as','it','its','that','this','what','which',
        'who','how','have','has','do','does','can','will',
    ]);

    /**
     * Client-side relevance filter: discard suggestions whose label
     * shares no meaningful word-prefix with the current phrase.
     */
    _filterRelevantSuggestions(suggestions) {
        const searchContext = this._getActiveSearchContext();
        const currentQuery = searchContext.active ? searchContext.searchTerm : '';
        if (!currentQuery) {
            const { phrase, word } = this._getCurrentPhrase();
            const fallback = phrase.length > word.length ? phrase : word;
            if (!fallback || fallback.length < 2 || !suggestions || suggestions.length === 0) {
                return suggestions || [];
            }
            return this._filterSuggestionsByQuery(suggestions, fallback);
        }
        return this._filterSuggestionsByQuery(suggestions, currentQuery);
    }

    _filterSuggestionsByQuery(suggestions, currentQuery) {
        if (!currentQuery || currentQuery.length < 2 || !suggestions || suggestions.length === 0) {
            return suggestions || [];
        }
        const queryWords = currentQuery
            .toLowerCase()
            .split(/\s+/)
            .filter(w => w.length >= 2 && !EntityInputGlobal._STOPWORDS.has(w));
        if (queryWords.length === 0) return suggestions;
        return suggestions.filter(entity => {
            const label = (entity.label || '').toLowerCase();
            return queryWords.some(qw => label.includes(qw));
        });
    }

    /**
     * Trigger analyze from an external control (e.g. notebook prompt toolbar).
     */
    triggerAnalyze() {
        this._handleAnalyzeClick(new Event('click'));
    }

    /**
     * Set suggestions for autocomplete.
     * Includes staleness guard: verifies suggestions are still relevant
     * to the current input before displaying.
     * @param {Array} suggestions - Array of { id, label, type, description? }
     */
    setSuggestions(suggestions) {
        const incoming = suggestions || [];
        this.highlightedIndex = 0;

        // Only render suggestions if there's text in the input
        const plainText = this.getPlainText();
        if (plainText.length < this.options.minSearchLength) {
            this.suggestions = [];
            this._hideSuggestions();
            return;
        }

        // Staleness guard: filter suggestions against the current query
        const relevant = this._filterRelevantSuggestions(incoming);
        this.suggestions = relevant;

        if (relevant.length > 0) {
            this._renderSuggestions();
        } else {
            this._hideSuggestions();
        }
    }
    
    /**
     * Set analysis results and apply them as inline entity links.
     * Replace matched text spans with badges based on positional data.
     * @param {Array} results - Array of { id, name, matched_text, entity_type, positions: [{start, end}] }
     */
    setAnalysisResults(results) {
        if (!this.inputEl || !results || results.length === 0) return;

        // Flatten all match positions
        const allPositions = [];
        results.forEach(entity => {
            if (entity.positions && entity.positions.length > 0) {
                entity.positions.forEach(pos => {
                    allPositions.push({
                        id: entity.id,
                        label: entity.name || entity.label || entity.matched_text,
                        type: entity.entity_type || entity.type || 'node',
                        matchedText: entity.matched_text || entity.label,
                        start: pos.start,
                        end: pos.end,
                    });
                });
            }
        });

        if (allPositions.length === 0) return;

        // Sort by start descending, remove overlaps (keep longer)
        allPositions.sort((a, b) => b.start - a.start);
        const filtered = [];
        for (const pos of allPositions) {
            const overlaps = filtered.some(
                f => !(pos.end <= f.start || pos.start >= f.end)
            );
            if (!overlaps) filtered.push(pos);
        }

        // Get current text (display form for positional replacement)
        const text = this.getDisplayQuery();
        if (!text) return;

        // Sort ascending for building
        filtered.sort((a, b) => a.start - b.start);

        // Clear input
        safeSetInnerHTML(this.inputEl, '');

        let lastEnd = 0;
        filtered.forEach(pos => {
            // Add text before this match
            if (pos.start > lastEnd) {
                this.inputEl.appendChild(document.createTextNode(text.substring(lastEnd, pos.start)));
            }

            if (this.options.searchTriggerChar && this.options.prefixBadgeWithTrigger) {
                this.inputEl.appendChild(document.createTextNode(this.options.searchTriggerChar));
            }

            const chip = this._createEntityChip({
                id: pos.id,
                label: pos.label,
                type: pos.type,
                matchedText: pos.matchedText || pos.label,
            });

            this.inputEl.appendChild(chip);
            lastEnd = pos.end;
        });

        // Add remaining text
        if (lastEnd < text.length) {
            this.inputEl.appendChild(document.createTextNode(text.substring(lastEnd)));
        }

        // Add trailing space for cursor
        this.inputEl.appendChild(document.createTextNode(' '));

        // Fire onChange
        if (this.options.onChange) {
            this.options.onChange({
                query: this.getFullText(),
                keywords: this.getEntities(),
                plainText: this.getPlainText(),
            });
        }
    }

    /**
     * Get plain text content (without entity labels)
     * @returns {string}
     */
    getPlainText() {
        if (!this.inputEl) return '';
        
        let text = '';
        this.inputEl.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent;
            } else if (this._isEntityChip(node)) {
                const entity = this._readEntityFromChip(node);
                text += entity?.matchedText || entity?.label || '';
            }
        });
        return text.trim();
    }
    
    /**
     * Get full text with entity labels included
     * @returns {string}
     */
    getFullText() {
        if (!this.inputEl) return '';
        
        let text = '';
        this.inputEl.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent;
            } else if (this._isEntityChip(node)) {
                const entity = this._readEntityFromChip(node);
                text += entity?.label || '';
            }
        });
        return text.replace(/\s+/g, ' ').trim();
    }
    
    /**
     * Get selected entities in order of appearance
     * @returns {Array<{id, label, type}>}
     */
    getEntities() {
        if (!this.inputEl) return [];
        
        const entities = [];
        this._forEachEntityNode((node) => {
            const entity = this._readEntityFromChip(node);
            if (entity?.id) {
                entities.push({
                    id: entity.id,
                    label: entity.label,
                    type: entity.type,
                });
            }
        });
        return entities;
    }
    
    /**
     * Clear the input
     */
    clear() {
        if (this.inputEl) {
            safeSetInnerHTML(this.inputEl, '');
        }
        this._hideSuggestions();
    }
    
    /**
     * Focus the input
     */
    focus() {
        if (this.inputEl) {
            this.inputEl.focus();
        }
    }
    
    /**
     * Set the theme
     * @param {string} theme - 'light' or 'dark'
     */
    setTheme(theme) {
        this.options.theme = theme;
        if (this.wrapperEl) {
            this.wrapperEl.dataset.theme = theme;
        }
    }
    
    /**
     * Destroy the component and clean up
     */
    destroy() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
        if (this._blurTimeout) {
            clearTimeout(this._blurTimeout);
        }
        if (this.container) {
            safeSetInnerHTML(this.container, '');
        }
        this.inputEl = null;
        this.suggestionsEl = null;
        this.container = null;
    }
    
    // ========================================
    // Aliases for React Component Compatibility
    // ========================================
    
    /**
     * Display query with entity labels inline (for analyze offsets).
     * @returns {string}
     */
    getDisplayQuery() {
        if (!this.inputEl) return '';

        let text = '';
        this.inputEl.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent;
            } else if (this._isEntityChip(node)) {
                const entity = this._readEntityFromChip(node);
                text += entity?.matchedText || entity?.label || '';
            }
        });
        return text.replace(/\s+/g, ' ').trim();
    }

    /**
     * Map API entity types to markdown link prefixes understood by the backend.
     * @param {string} entityType
     * @returns {string}
     */
    static toMarkdownLinkType(entityType) {
        const map = {
            taxonomy_resource: 'resource',
            ontology_type: 'edge',
            taxonomy_type: 'type',
            concept: 'concept',
            node: 'node',
            edge: 'edge',
        };
        return map[entityType] || entityType || 'node';
    }

    /**
     * Full query with entity badges as markdown links — matches React getFullQuery().
     * @returns {string}
     */
    getFullQuery() {
        if (!this.inputEl) return '';

        let text = '';
        this.inputEl.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent;
            } else if (this._isEntityChip(node)) {
                const entity = this._readEntityFromChip(node);
                const label = entity?.label || '';
                const id = entity?.id || '';
                const type = EntityInputGlobal.toMarkdownLinkType(entity?.type || 'node');
                text += `[${label}](${type}:${id})`;
            }
        });
        return text.replace(/\s+/g, ' ').trim();
    }
    
    /**
     * Alias for getEntities() - matches React component's getSelectedEntities()
     * @returns {Array<{id, label, type}>}
     */
    getSelectedEntities() {
        return this.getEntities();
    }
}

// Register on window for global access
if (typeof window !== 'undefined') {
    window.EntityInput = EntityInputGlobal;
}

export default EntityInputGlobal;
